"use client";

import { FC, useEffect, useRef, useState } from "react";
import { Content } from "@prismicio/client";
import { PrismicRichText, SliceComponentProps } from "@prismicio/react";
import Bounded from "@/components/componentsV2/shared-components/Bounded";
import { PrismicNextImage } from "@prismicio/next";
import { motion } from "framer-motion";

/**
 * Props for `LifeAtZenithAcademySection`.
 */
export type LifeAtZenithAcademySectionProps =
  SliceComponentProps<Content.LifeAtZenithAcademySectionSlice>;

/**
 * Component for "LifeAtZenithAcademySection" Slices.
 */
const LifeAtZenithAcademySection: FC<LifeAtZenithAcademySectionProps> = ({
  slice,
}) => {
  const ringRef = useRef(null);
  const requestRef = useRef<number>();
  const previousTimeRef = useRef<number>();
  const [rotation, setRotation] = useState(0);

  const config = {
    totalSlides: 16,
    radius: 1600,
    slideHeight: 850,
    slideWidth: 500,
    autoRotate: true,
    rotationSpeed: 0.05,
    rotationDirection: 1,
  };

  const originalSlides = slice?.primary?.images || [];
  const slidesToUse = [];

  if (originalSlides.length > 0) {
    for (let i = 0; i < config.totalSlides; i++) {
      slidesToUse.push(originalSlides[i % originalSlides.length]);
    }
  }

  const animate = (time: number) => {
    if (previousTimeRef.current !== undefined && config.autoRotate) {
      const deltaTime = time - previousTimeRef.current;
      const rotationAmount =
        (deltaTime / 16.67) * config.rotationSpeed * config.rotationDirection;
      setRotation((prev) => prev + rotationAmount);
    }
    previousTimeRef.current = time;
    requestRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    requestRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(requestRef.current!);
  }, []);

  const getSlidePosition = (index: number) => {
    const angle = (index / config.totalSlides) * 360;
    const rad = (angle * Math.PI) / 180;

    const x = Math.sin(rad) * config.radius;
    const z = Math.cos(rad) * config.radius;
    const rotateY = -angle;

    return {
      x,
      z: -z,
      rotateY: `${rotateY}deg`,
    };
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="bg-[#EBF7FF] py-[120px]"
    >
      {/* <Bounded as="div" className=""> */}
        <div className="flex flex-col items-center justify-center overflow-hidden">
          <div className="mb-3 rounded-[50px] w-fit flex items-center gap-2.5 justify-center px-4 py-2.5 border border-[#6AC1F7] bg-white">
            <PrismicNextImage field={slice.primary.badge_icon} />
            <div className="text-[#045B91] text-center font-archivo text-base font-normal leading-6">
              {slice.primary.badge_content}
            </div>
          </div>
          <motion.div
            className="font-archivo font-semibold text-5xl leading-16 text-[#033D61] mb-4 text-center"
            initial={{
              opacity: 0,
              y: 30,
            }}
            whileInView={{
              opacity: 1,
              y: 0,
            }}
            viewport={{ once: true }}
            transition={{
              duration: 0.8,
            }}
          >
            <PrismicRichText
              field={slice.primary.title}
              components={{
                label: ({ node, children }) => {
                  if (node.data.label === "Highlight") {
                    return <span className="text-secondary">{children}</span>;
                  }
                  return <span className="text-primary">{children}</span>;
                },
              }}
            />
          </motion.div>
          <motion.div
            className="font-merriweather font-normal text-xl leading-8 text-center text-[#033D61] mb-10.5"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <PrismicRichText field={slice.primary.description} />
          </motion.div>
          <div className="relative w-full h-[67vh]">
            <div
              className="absolute inset-0 z-10"
              style={{
                perspective: "1000px",
              }}
            >
              <motion.div
                ref={ringRef}
                className="absolute top-1/2 left-1/2"
                style={{
                  width: `${config.slideWidth}px`,
                  height: `${config.slideHeight}px`,
                  x: -config.slideWidth / 2,
                  y: -config.slideHeight / 2,
                  rotateY: rotation,
                  transformStyle: "preserve-3d",
                }}
              >
                {slidesToUse.map((imageContent, index) => {
                  const position = getSlidePosition(index);

                  return (
                    <motion.div
                      key={index}
                      className="absolute overflow-hidden rounded-2xl shadow-2xl"
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{
                        opacity: 1,
                        scale: 1,
                        width: `${config.slideWidth}px`,
                        height: `${config.slideHeight}px`,
                        x: position.x,
                        z: position.z,
                        rotateY: position.rotateY,
                        transformStyle: "preserve-3d",
                        backfaceVisibility: "hidden",
                      }}
                      transition={{
                        duration: 0.8,
                        delay: index * 0.1,
                        ease: "easeOut",
                      }}
                      whileHover={{
                        scale: 1.1,
                        z: position.z - 50,
                        transition: { duration: 0.3 },
                      }}
                    >
                      <PrismicNextImage
                        field={imageContent.image}
                        className="w-full h-full object-cover"
                        width={config.slideWidth}
                        height={config.slideHeight}
                      />
                    </motion.div>
                  );
                })}
              </motion.div>
            </div>
          </div>
        </div>
      {/* </Bounded> */}
    </section>
  );
};

export default LifeAtZenithAcademySection;
